// pages/admin/ShippingList.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
    getAllShippingsAPI,
    updateShippingStatusAPI,
} from "../../../../api/adminShipping.ts"; // Adjust path
import {
    BlockBetween,
    BlockContent,
    BlockDes,
    BlockHead,
    BlockHeadContent,
    BlockTitle,
    Icon,
    PreviewCard,
    ReactDataTable,
} from "../../../../components/Component";
import Content from "../../../../layout/content/Content";
import Head from "../../../../layout/head/Head";

const ShippingList = () => {
    const [shippings, setShippings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedShipping, setSelectedShipping] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchShippings = async () => {
            try {
                setLoading(true);
                const response = await getAllShippingsAPI();
                setShippings(response.data.data);
            } catch (error) {
                console.error("Failed to fetch shippings:", error);
                toast.error("Failed to fetch shippings");
            } finally {
                setLoading(false);
            }
        };

        fetchShippings();
    }, []);

    const toggleStatusDropdown = (shippingId) => {
        setStatusDropdownOpen((prev) => ({
            ...prev,
            [shippingId]: !prev[shippingId],
        }));
    };

    const handleStatusUpdate = async (shippingId, newStatus) => {
        try {
            await updateShippingStatusAPI({
                id: shippingId,
                body: { shippingStatus: newStatus },
            });
            setShippings((prevShippings) =>
                prevShippings.map((shipping) =>
                    shipping._id === shippingId ? { ...shipping, shippingStatus: newStatus } : shipping
                )
            );
            if (selectedShipping && selectedShipping._id === shippingId) {
                setSelectedShipping((prev) => ({ ...prev, shippingStatus: newStatus }));
            }
            toast.success(`Shipping ${shippingId} status updated to ${newStatus}`);
        } catch (error) {
            console.error("Failed to update shipping status:", error);
            toast.error("Failed to update shipping status");
        }
    };

    const toggleModal = () => setModalOpen((prev) => !prev);

    const handleViewShipping = (shipping) => {
        setSelectedShipping(shipping);
        toggleModal();
    };

    const statusOptions = ["Pending", "Shipped", "In Transit", "Delivered", "Cancelled"];

    const columns = [
        {
            name: "Shipping ID",
            selector: (row) => row._id,
            cell: (row) => (
                <span className="fw-bold text-primary cursor-pointer" onClick={() => navigate(`/admin/shippings/${row._id}`)}>
                    {row._id}
                </span>
            ),
        },
        {
            name: "User",
            selector: (row) => `${row.userId.first_name} ${row.userId.last_name}`,
            cell: (row) => (
                <span className="text-muted">{`${row.userId.first_name} ${row.userId.last_name}`}</span>
            ),
            sortable: true,
        },
        {
            name: "Order ID",
            selector: (row) => row.orderId?._id || "N/A",
            cell: (row) => <span>{row.orderId?._id || "N/A"}</span>,
        },
        {
            name: "Address",
            selector: (row) => row.addressSnapshot?.addressLine1 || "N/A",
            cell: (row) => (
                <span>
                    {row.addressSnapshot
                        ? `${row.addressSnapshot.addressLine1 || ""}, ${row.addressSnapshot.city || "N/A"}, ${row.addressSnapshot.state || "N/A"}`
                        : "N/A"}
                </span>
            ),
        },
        {
            name: "Tracking",
            selector: (row) => row.trackingNumber || "N/A",
            cell: (row) => <span>{row.trackingNumber || "N/A"}</span>,
        },
        {
            name: "Status",
            selector: (row) => row.shippingStatus,
            cell: (row) => (
                <Dropdown
                    isOpen={statusDropdownOpen[row._id] || false}
                    toggle={() => toggleStatusDropdown(row._id)}
                >
                    <DropdownToggle
                        caret
                        color={
                            row.shippingStatus === "Delivered"
                                ? "success"
                                : row.shippingStatus === "Cancelled"
                                    ? "danger"
                                    : "warning"
                        }
                        className="btn-sm"
                    >
                        {row.shippingStatus}
                    </DropdownToggle>
                    <DropdownMenu>
                        {statusOptions.map((status) => (
                            <DropdownItem
                                key={status}
                                onClick={() => handleStatusUpdate(row._id, status)}
                                disabled={row.shippingStatus === status}
                            >
                                {status}
                            </DropdownItem>
                        ))}
                    </DropdownMenu>
                </Dropdown>
            ),
            sortable: true,
        },
        {
            name: "Estimated Delivery",
            selector: (row) => row.estimatedDeliveryDate || "N/A",
            cell: (row) => (
                <span>
                    {row.estimatedDeliveryDate
                        ? new Date(row.estimatedDeliveryDate).toLocaleDateString()
                        : "N/A"}
                </span>
            ),
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <Button
                    color="info"
                    size="sm"
                    onClick={() => handleViewShipping(row)}
                >
                    <Icon name="eye" /> View
                </Button>
            ),
        },
    ];

    return (
        <React.Fragment>
            <Head title="Shipping List" />
            <Content>
                <BlockHead size="sm">
                    <BlockBetween>
                        <BlockHeadContent>
                            <BlockTitle tag="h3" page>
                                Shipping List
                            </BlockTitle>
                            <BlockDes className="text-soft">
                                <p>You have a total of {shippings.length} shipping records.</p>
                            </BlockDes>
                        </BlockHeadContent>
                    </BlockBetween>
                </BlockHead>
                <BlockContent>
                    <PreviewCard>
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                                <div className="text-center">
                                    <div
                                        className="spinner-grow text-primary"
                                        style={{ width: "3rem", height: "3rem" }}
                                        role="status"
                                    >
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted">Loading shipping records, please wait...</p>
                                </div>
                            </div>
                        ) : (
                            <ReactDataTable
                                data={shippings}
                                columns={columns}
                                pagination
                                actions
                                expandableRows
                            />
                        )}
                    </PreviewCard>
                </BlockContent>

                {/* View Shipping Modal */}
                <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
                    <ModalHeader toggle={toggleModal} className="bg-primary text-white">
                        Shipping Details - {selectedShipping?._id}
                    </ModalHeader>
                    <ModalBody>
                        {selectedShipping ? (
                            <div>
                                <div className="mb-3">
                                    <h6 className="text-muted">User</h6>
                                    <p className="fw-bold">{`${selectedShipping.userId.first_name} ${selectedShipping.userId.last_name}`}</p>
                                    <p className="text-muted">{selectedShipping.userId.email}</p>
                                </div>
                                <div className="mb-3">
                                    <h6 className="text-muted">Order ID</h6>
                                    <p>{selectedShipping.orderId?._id || "N/A"}</p>
                                </div>
                                <div className="mb-3">
                                    <h6 className="text-muted">Address</h6>
                                    <p>
                                        {selectedShipping.addressSnapshot
                                            ? `${selectedShipping.addressSnapshot.addressLine1 || ""}, ${selectedShipping.addressSnapshot.city || "N/A"}, ${selectedShipping.addressSnapshot.state || "N/A"}, ${selectedShipping.addressSnapshot.postalCode || "N/A"}, ${selectedShipping.addressSnapshot.country || "N/A"}`
                                            : "N/A"}
                                    </p>
                                </div>
                                <div className="mb-3">
                                    <h6 className="text-muted">Tracking Number</h6>
                                    <p>{selectedShipping.trackingNumber || "N/A"}</p>
                                </div>
                                <div className="mb-3">
                                    <h6 className="text-muted">Courier Service</h6>
                                    <p>{selectedShipping.courierService || "N/A"}</p>
                                </div>
                                <div className="mb-3">
                                    <h6 className="text-muted">Status</h6>
                                    <span className={`badge ${selectedShipping.shippingStatus === "Delivered" ? "bg-success" : "bg-warning"} text-white`}>
                                        {selectedShipping.shippingStatus}
                                    </span>
                                </div>
                                <div className="mb-3">
                                    <h6 className="text-muted">Estimated Delivery</h6>
                                    <p>{selectedShipping.estimatedDeliveryDate ? new Date(selectedShipping.estimatedDeliveryDate).toLocaleString() : "N/A"}</p>
                                </div>
                                <div>
                                    <h6 className="text-muted">Created At</h6>
                                    <p>{new Date(selectedShipping.createdAt).toLocaleString()}</p>
                                </div>
                            </div>
                        ) : (
                            <p>Loading shipping details...</p>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggleModal}>
                            Close
                        </Button>
                        <Button color="primary" onClick={() => navigate(`/admin/shippings/${selectedShipping?._id}`)}>
                            Full Details
                        </Button>
                    </ModalFooter>
                </Modal>
            </Content>
        </React.Fragment>
    );
};

export default ShippingList;